import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { colors, radius } from '../theme';

// DNA Channel (2026-10-07): analisa pola channel YouTube acuan dari data publik.
// Backend: routes/dna.js + lib/channel-dna.js di starhub-creative-api.
const API = 'https://api.starhubcreative.id';

const fmt = (v) => {
  const n = Number(v);
  if (!isFinite(n)) return '-';
  if (n >= 1e6) return (n / 1e6).toLocaleString('id-ID', { maximumFractionDigits: 1 }) + ' jt';
  if (n >= 1e3) return (n / 1e3).toLocaleString('id-ID', { maximumFractionDigits: 1 }) + ' rb';
  return n.toLocaleString('id-ID');
};
const hh = (h) => `${String(h).padStart(2, '0')}.00`;
const sectionTitle = { fontSize: 12, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 };
const chip = { fontSize: 11, padding: '3px 8px', borderRadius: 6, background: colors.bgCardMuted, border: `1px solid ${colors.border}`, color: colors.textPrimary, whiteSpace: 'nowrap' };

function StatusPill({ status }) {
  const map = {
    processing: { c: colors.accent, bg: colors.accentMuted, t: 'Menganalisa', i: 'ti-loader-2' },
    ready: { c: colors.success, bg: colors.successMuted, t: 'Siap', i: 'ti-check' },
    failed: { c: colors.danger, bg: colors.dangerMuted, t: 'Gagal', i: 'ti-alert-triangle' },
  };
  const s = map[status] || map.processing;
  return (
    <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 6, background: s.bg, color: s.c, display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
      <i className={`ti ${s.i}`} style={{ fontSize: 13 }}></i>{s.t}
    </span>
  );
}

// Label "layak ditiru" (2026-10-07) — dihitung server di lib/dna-compare.js assessWorthiness().
const WORTH_STYLE = {
  layak: { c: colors.success, bg: colors.successMuted, i: 'ti-thumb-up' },
  cukup: { c: colors.warning, bg: colors.warningMuted, i: 'ti-scale' },
  kurang: { c: colors.textSecondary, bg: colors.bgCardMuted, i: 'ti-thumb-down' },
};
const GRADE = { good: { c: colors.success, i: 'ti-circle-check', t: 'bagus' }, ok: { c: colors.warning, i: 'ti-circle-minus', t: 'sedang' }, weak: { c: colors.danger, i: 'ti-circle-x', t: 'lemah' } };

function WorthPill({ w }) {
  if (!w) return null;
  const s = WORTH_STYLE[w.level] || WORTH_STYLE.cukup;
  return (
    <span title={w.checks.map(c => `${c.label}: ${c.value}`).join('\n')}
      style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 6, background: s.bg, color: s.c, display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
      <i className={`ti ${s.i}`} style={{ fontSize: 13 }}></i>{w.label}
    </span>
  );
}

function WorthBox({ w }) {
  if (!w) return null;
  return (
    <div>
      <div style={sectionTitle}>Layak ditiru?</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <WorthPill w={w} />
        <span style={{ fontSize: 12, color: colors.textSecondary }}>
          {w.level === 'layak' ? 'Formula channel ini sedang didorong algoritma dan masih jalan sekarang.'
            : w.level === 'cukup' ? 'Sebagian polanya masih bisa dipakai, tapi ada sisi yang lemah. Lihat rinciannya.'
            : 'Channel ini sedang tidak didorong algoritma. Cari acuan lain yang lebih panas.'}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {w.checks.map(c => {
          const g = GRADE[c.grade] || GRADE.ok;
          return (
            <div key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
              <i className={`ti ${g.i}`} style={{ color: g.c, fontSize: 15, flexShrink: 0 }}></i>
              <span style={{ fontWeight: 600, color: colors.textPrimary, minWidth: 130 }}>{c.label}</span>
              <span style={{ color: colors.textSecondary }}>{c.value} · {g.t}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Saran kata kunci channel (2026-10-07). YouTube API kita tidak punya izin mengubah setelan
// channel, jadi klien menyalin lalu menempel sendiri di YouTube Studio.
function KeywordBox({ kw }) {
  const [copied, setCopied] = useState(false);
  if (!kw || !kw.keywords?.length) return null;
  const text = kw.keywords.join(', ');
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2500); }
    catch (e) { setCopied(false); }
  };
  return (
    <div>
      <div style={sectionTitle}>Saran kata kunci channel</div>
      <div style={{ fontSize: 12, color: colors.textSecondary, marginBottom: 8 }}>
        {kw.source === 'channel' ? 'Diambil dari kata kunci channel acuan' : kw.source === 'channel+judul' ? 'Diambil dari kata kunci channel acuan, ditambah hashtag dan judulnya' : 'Channel acuan tidak mengisi kata kunci, jadi diambil dari hashtag dan judulnya'}
        {kw.removedBrand ? `. ${kw.removedBrand} kata kunci berisi nama channel acuan sudah dibuang.` : '.'}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
        {kw.keywords.map(k => <span key={k} style={chip}>{k}</span>)}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button onClick={copy}
          style={{ padding: '7px 12px', borderRadius: 8, border: `1px solid ${colors.borderStrong}`, background: colors.bgCard, color: colors.textPrimary, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <i className={`ti ${copied ? 'ti-check' : 'ti-copy'}`} style={{ fontSize: 14 }}></i>{copied ? 'Tersalin' : 'Salin semua'}
        </button>
        <span style={{ fontSize: 12, color: colors.textSecondary }}>
          Tempel di YouTube Studio → <b>Setelan</b> → <b>Channel</b> → <b>Info dasar</b> → <b>Kata kunci</b>. Tambahkan nama channel kamu sendiri, dan hapus nama penyanyi yang tidak ada di kontenmu.
        </span>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={{ background: colors.bgCardMuted, borderRadius: radius.md, padding: '10px 12px' }}>
      <div style={{ fontSize: 11, color: colors.textSecondary }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: colors.textPrimary, marginTop: 2 }}>{value}</div>
    </div>
  );
}

function ApplyTime({ dna, ytChannels, onApplied }) {
  const [picked, setPicked] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [open, setOpen] = useState(false);
  const best = dna.profile?.timing?.bestHourWib;
  if (best == null) return null;
  const active = ytChannels.filter(c => c.is_active);

  const toggle = (id) => setPicked(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const apply = async () => {
    if (!picked.length) return;
    if (!window.confirm(`Set jam posting ${picked.length} channel ke ${hh(best)} WIB?\n\nSetelah ini, video ke channel tersebut tidak langsung tayang. Upload ditahan dan dijalankan jam ${hh(best)} WIB berikutnya.`)) return;
    setSaving(true); setMsg('');
    try {
      const r = await axios.post(`${API}/api/dna/${dna.id}/apply-time`, { youtube_channel_ids: picked });
      setMsg(`Jam posting ${r.data.updated.length} channel diset ke ${hh(best)} WIB.`);
      setPicked([]);
      onApplied();
    } catch (e) {
      setMsg(e.response?.data?.error || 'Gagal menyimpan jam posting');
    }
    setSaving(false);
  };

  // Tertutup secara bawaan: ini pengaturan opsional, bukan sesuatu yang wajib diisi.
  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        style={{ marginTop: 12, padding: '8px 12px', borderRadius: 8, border: `1px solid ${colors.borderStrong}`, background: colors.bgCard, color: colors.textPrimary, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <i className="ti ti-clock-play" style={{ fontSize: 15 }}></i>Atur jam upload channel (opsional)
      </button>
    );
  }

  return (
    <div style={{ marginTop: 12, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary }}>Ikuti jam upload {hh(best)} WIB (opsional)</div>
        <button onClick={() => { setOpen(false); setPicked([]); setMsg(''); }} style={{ background: 'transparent', border: 'none', color: colors.textSecondary, fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>Tutup</button>
      </div>
      <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 4, lineHeight: 1.5 }}>
        Daftar di bawah adalah channel YouTube milikmu, satu baris satu channel. Centang channel yang ingin videonya
        ditayangkan jam {hh(best)} WIB, lalu tekan tombol. Channel yang tidak dicentang tidak berubah. Kolom kanan menunjukkan pengaturan channel itu sekarang.
      </div>
      {active.length === 0 ? (
        <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 8 }}>Belum ada channel YouTube yang tersambung.</div>
      ) : (
        <div style={{ marginTop: 10, border: `1px solid ${colors.border}`, borderRadius: radius.sm }}>
          <div style={{ display: 'flex', gap: 10, padding: '6px 10px', fontSize: 11, fontWeight: 600, color: colors.textSecondary, background: colors.bgCardMuted, borderBottom: `1px solid ${colors.border}` }}>
            <span style={{ width: 16, flexShrink: 0 }}></span>
            <span style={{ flex: 1 }}>Channel YouTube kamu</span>
            <span style={{ flexShrink: 0 }}>Sekarang</span>
          </div>
          <div style={{ maxHeight: 260, overflowY: 'auto' }}>
            {active.map(c => (
              <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: colors.textPrimary, cursor: 'pointer', padding: '7px 10px', borderBottom: `1px solid ${colors.border}` }}>
                {/* App.css memberi semua input width:100% + padding — checkbox wajib dinetralkan,
                    kalau tidak ia memakan seluruh baris dan nama channel tidak terlihat. */}
                <input type="checkbox" checked={picked.includes(c.id)} onChange={() => toggle(c.id)}
                  style={{ width: 16, height: 16, padding: 0, margin: 0, flexShrink: 0, accentColor: colors.accent }} />
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.channel_name}</span>
                <span style={{ fontSize: 11, color: colors.textSecondary, flexShrink: 0 }}>
                  {c.default_post_time ? `ditahan ke ${String(c.default_post_time).slice(0, 5).replace(':', '.')} WIB` : 'langsung tayang'}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
        <button onClick={apply} disabled={saving || !picked.length}
          style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: (saving || !picked.length) ? colors.border : colors.accent, color: '#FFFFFF', fontSize: 13, fontWeight: 600, cursor: (saving || !picked.length) ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <i className="ti ti-clock-play" style={{ fontSize: 15 }}></i>{saving ? 'Menyimpan...' : picked.length ? `Tayangkan ${picked.length} channel jam ${hh(best)} WIB` : 'Centang channel dulu'}
        </button>
        {msg && <span style={{ fontSize: 12, color: colors.textSecondary }}>{msg}</span>}
      </div>
    </div>
  );
}

// Pembanding channel kita vs acuan (2026-10-07). Channel sendiri dianalisa dengan cara yang
// sama (data publik), disimpan 24 jam di server.
function CompareBox({ dna, ytChannels }) {
  const active = ytChannels.filter(c => c.is_active);
  const [ycId, setYcId] = useState('');
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const pollRef = useRef(null);
  useEffect(() => () => clearTimeout(pollRef.current), []);

  const poll = (id) => {
    pollRef.current = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/api/dna/${dna.id}/compare/${id}`);
        if (r.data.status === 'processing') return poll(id);
        setRes(r.data); setBusy(false);
      } catch (e) { setBusy(false); setErr('Gagal membaca hasil perbandingan'); }
    }, 3000);
  };
  const run = async () => {
    if (!ycId) return;
    clearTimeout(pollRef.current);
    setBusy(true); setErr(''); setRes(null);
    try {
      const r = await axios.post(`${API}/api/dna/${dna.id}/compare`, { youtube_channel_id: ycId });
      if (r.data.status === 'processing') return poll(ycId);
      setRes(r.data);
    } catch (e) {
      setErr(e.response?.data?.error || 'Gagal membandingkan');
    }
    setBusy(false);
  };

  const statusStyle = s => s === 'ok' ? { color: colors.success, label: 'Sudah dekat' } : s === 'gap' ? { color: colors.warning, label: 'Tertinggal' } : null;
  return (
    <div>
      <div style={sectionTitle}>Bandingkan dengan channel saya</div>
      <div style={{ fontSize: 12, color: colors.textSecondary, marginBottom: 8 }}>
        Pilih salah satu channel YouTube kamu. Channel itu dibaca dengan cara yang sama seperti channel acuan (data publik, gratis), lalu dibandingkan baris per baris.
      </div>
      {active.length === 0 ? (
        <div style={{ fontSize: 12, color: colors.textSecondary }}>Belum ada channel YouTube yang tersambung.</div>
      ) : (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <select id={`cmp-${dna.id}`} aria-label="Channel yang dibandingkan" value={ycId} onChange={e => { setYcId(e.target.value); setRes(null); }}
            style={{ width: 'auto', minWidth: 200, maxWidth: '100%', padding: '8px 10px', fontSize: 13, borderRadius: 8 }}>
            <option value="">Pilih channel kamu</option>
            {active.map(c => <option key={c.id} value={c.id}>{c.channel_name}</option>)}
          </select>
          <button onClick={run} disabled={!ycId || busy}
            style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: (!ycId || busy) ? colors.border : colors.accent, color: '#FFFFFF', fontSize: 13, fontWeight: 600, cursor: (!ycId || busy) ? 'not-allowed' : 'pointer' }}>
            {busy ? 'Membaca channel kamu...' : 'Bandingkan'}
          </button>
        </div>
      )}
      {err && <div style={{ fontSize: 12, color: colors.danger, marginTop: 8 }}>{err}</div>}
      {res?.status === 'failed' && <div style={{ fontSize: 12, color: colors.danger, marginTop: 8 }}>{res.error || 'Channel kamu tidak bisa dibaca.'}</div>}
      {res?.status === 'ready' && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {res.summary?.close != null && (
            <div style={{ fontSize: 13, color: colors.textPrimary }}>
              <b>{res.own.name}</b> sudah dekat dengan pola <b>{res.ref.name}</b> di {res.summary.scored - res.summary.gaps} dari {res.summary.scored} hal yang dinilai.
            </div>
          )}
          <div style={{ overflowX: 'auto', border: `1px solid ${colors.border}`, borderRadius: radius.sm }}>
            <table style={{ width: '100%', minWidth: 520, borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: colors.bgCardMuted, color: colors.textSecondary, textAlign: 'left' }}>
                  <th style={{ padding: '7px 10px', fontWeight: 600 }}>Hal</th>
                  <th style={{ padding: '7px 10px', fontWeight: 600 }}>{res.own.name}</th>
                  <th style={{ padding: '7px 10px', fontWeight: 600 }}>{res.ref.name}</th>
                  <th style={{ padding: '7px 10px', fontWeight: 600 }}>Nilai</th>
                </tr>
              </thead>
              <tbody>
                {res.rows.map(r => {
                  const st = statusStyle(r.status);
                  return (
                    <tr key={r.key} style={{ borderTop: `1px solid ${colors.border}` }}>
                      <td style={{ padding: '7px 10px', color: colors.textSecondary }}>{r.label}</td>
                      <td style={{ padding: '7px 10px', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{r.own}</td>
                      <td style={{ padding: '7px 10px', fontVariantNumeric: 'tabular-nums' }}>{r.ref}</td>
                      <td style={{ padding: '7px 10px', whiteSpace: 'nowrap' }}>
                        {st ? <span style={{ color: st.color, fontWeight: 600 }}><i className={`ti ${r.status === 'ok' ? 'ti-check' : 'ti-alert-triangle'}`} style={{ marginRight: 4 }}></i>{st.label}</span> : <span style={{ color: colors.textMuted }}>info</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Yang bisa dilakukan</div>
            <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: colors.textPrimary }}>
              {res.tips.map((t, i) => <li key={i}>{t}</li>)}
            </ul>
          </div>
          <div style={{ fontSize: 11, color: colors.textMuted }}>Data channel kamu dibaca {ago(res.analyzed_at)} dan disimpan 24 jam.</div>
        </div>
      )}
    </div>
  );
}

function DnaDetail({ dna, ytChannels, reloadChannels, onUseInUgc }) {
  const p = dna.profile;
  if (!p) return null;
  const s = p.seo || {}, t = p.timing || {}, f = p.format || {}, sc = p.scale || {}, perf = p.performance || {};
  const hours = [...(t.hours || [])].sort((a, b) => b.count - a.count).slice(0, 5);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, paddingTop: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 8 }}>
        <Stat label="Subscriber" value={fmt(p.channel?.subscribers)} />
        <Stat label="Median views/video" value={fmt(sc.medianViews)} />
        <Stat label="Median ÷ subscriber" value={sc.medianPerSubscriber != null ? `${sc.medianPerSubscriber.toLocaleString('id-ID')}×` : '-'} />
        <Stat label="Durasi median" value={f.durationMedianMin != null ? `${f.durationMedianMin.toLocaleString('id-ID')} mnt` : '-'} />
        <Stat label="Porsi Shorts" value={`${f.shortsShare ?? 0}%`} />
        <Stat label="Upload/minggu" value={t.perWeek != null ? t.perWeek.toLocaleString('id-ID') : '-'} />
      </div>

      <WorthBox w={dna.worthiness} />

      <div>
        <div style={sectionTitle}>Format</div>
        <div style={{ fontSize: 13, color: colors.textPrimary }}>
          {f.isCompilation
            ? `Kompilasi panjang, sekitar ${Math.round(f.durationMedianMin)} menit per video. Cocok dengan fitur UGC kompilasi.`
            : f.shortsShare >= 50 ? 'Didominasi Shorts.' : `Video pendek, sekitar ${f.durationMedianMin} menit per video.`}
          {f.shortsMissing ? ' Channel ini tidak membuat Shorts sama sekali.' : ''}
        </div>
      </div>

      {Array.isArray(p.songs?.hot) && (
        <div>
          <div style={sectionTitle}>Lagu yang sering dipakai</div>
          {p.songs.hot.length ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {p.songs.hot.slice(0, 15).map(s => <span key={s.title} style={chip}>{s.title} · {s.videos} video</span>)}
            </div>
          ) : (
            <div style={{ fontSize: 13, color: colors.textSecondary }}>Channel ini tidak menulis nama lagu di judul atau deskripsi, jadi daftar lagunya tidak bisa dibaca.</div>
          )}
          <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 6 }}>Di proyek UGC yang memakai DNA ini, daftar ini muncul di atas daftar lagu lengkap dengan tombol tambah.</div>
        </div>
      )}

      <div>
        <div style={sectionTitle}>Judul & SEO</div>
        <div style={{ fontSize: 13, color: colors.textPrimary, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div>
            Rata-rata {s.titleAvgChars} karakter
            {s.pctMostlyCaps >= 50 ? `, ${s.pctMostlyCaps}% huruf kapital` : ''}
            {s.pctYear >= 30 ? `, ${s.pctYear}% memuat tahun` : ''}
            {s.pctEmoji >= 30 ? `, ${s.pctEmoji}% pakai emoji` : ''}
            {s.mainSeparator ? `, dipisah "${s.mainSeparator}"` : ''}.
          </div>
          {s.coreWords?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: colors.textSecondary, marginRight: 4 }}>Kata kunci tetap:</span>
              {s.coreWords.slice(0, 12).map(w => <span key={w} style={chip}>{w}</span>)}
            </div>
          )}
          {s.topHashtags?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: colors.textSecondary, marginRight: 4 }}>Hashtag:</span>
              {s.topHashtags.slice(0, 10).map(([h]) => <span key={h} style={chip}>{h}</span>)}
            </div>
          )}
          {s.bestTitles?.length > 0 && (
            <div>
              <div style={{ fontSize: 12, color: colors.textSecondary, marginBottom: 4 }}>Judul terlaris:</div>
              {s.bestTitles.map((x, i) => (
                <div key={i} style={{ fontSize: 12, background: colors.bgCardMuted, borderRadius: 6, padding: '6px 10px', marginBottom: 4, overflowWrap: 'anywhere' }}>{x}</div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div>
        <div style={sectionTitle}>Jam upload</div>
        <div style={{ fontSize: 13, color: colors.textPrimary }}>
          {t.bestHourWib != null
            ? <>Jam terbaik <b>{hh(t.bestHourWib)} WIB</b>, dari {t.sample} video terbaru. Jeda antar upload sekitar {t.medianGapDays} hari.</>
            : 'Belum ada data jam upload.'}
        </div>
        {hours.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 8 }}>
            {hours.map(h => <span key={h.hour} style={chip}>{hh(h.hour)} · {h.count} video · {fmt(h.medianPerDay)}/hari</span>)}
          </div>
        )}
        <ApplyTime dna={dna} ytChannels={ytChannels} onApplied={reloadChannels} />
      </div>

      {perf.outliers?.length > 0 && (
        <div>
          <div style={sectionTitle}>Video pencilan (≥3× median)</div>
          {perf.outliers.slice(0, 5).map(o => (
            <a key={o.id} href={`https://www.youtube.com/watch?v=${o.id}`} target="_blank" rel="noopener noreferrer"
              style={{ display: 'flex', gap: 10, alignItems: 'baseline', padding: '7px 0', borderBottom: `1px solid ${colors.border}`, textDecoration: 'none', color: colors.textPrimary }}>
              <span style={{ fontSize: 12, flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{o.title}</span>
              <span style={{ fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{fmt(o.views)}</span>
              <span style={{ fontSize: 11, color: colors.textSecondary, flexShrink: 0 }}>{o.xMedian}×</span>
            </a>
          ))}
        </div>
      )}

      <KeywordBox kw={dna.keywords} />

      <CompareBox dna={dna} ytChannels={ytChannels} />

      <div style={{ fontSize: 12, color: colors.textPrimary, background: colors.accentMuted, borderRadius: radius.md, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontWeight: 700 }}><i className="ti ti-dna-2" style={{ marginRight: 6 }}></i>Cara memakai DNA ini di proyek UGC</div>
        <ol style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4, color: colors.textSecondary }}>
          <li>Buka menu <b>UGC Compilation</b>. Di kotak <b>Proyek Baru</b>, pilih <b>"Ikuti DNA: {dna.channel_name}"</b> di pilihan <i>Gaya judul &amp; deskripsi</i>, lalu buat proyek. Judul dan deskripsi AI akan mengikuti pola channel ini.</li>
          <li>Di detail proyek, kotak <b>Saran dari DNA</b> (di atas daftar lagu) menunjukkan target panjang dan lagu yang sering dipakai channel ini.</li>
          <li>Di bagian <b>Thumbnail YouTube</b>, kotak <b>Template dari DNA</b> membuat thumbnail dengan gaya channel ini, gratis.</li>
        </ol>
        <div style={{ color: colors.textSecondary }}>Yang ditiru polanya, bukan judul, gambar, atau videonya.</div>
        {onUseInUgc && (
          <div>
            <button onClick={() => onUseInUgc(dna.id)}
              style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: colors.accent, color: '#FFFFFF', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <i className="ti ti-movie" style={{ fontSize: 15 }}></i>Pakai di proyek UGC baru
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const ago = (iso) => {
  if (!iso) return 'belum pernah';
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 60) return `${m} menit lalu`;
  const h = Math.round(m / 60);
  return h < 48 ? `${h} jam lalu` : `${Math.round(h / 24)} hari lalu`;
};

// Pantauan otomatis (dna-monitor.js di server, tiap 2 jam): video channel acuan yang baru
// tayang dan views/harinya >= 3x biasanya.
function AlertsBox({ alerts, onDismiss, onDismissAll }) {
  if (!alerts.length) {
    return (
      <div style={{ fontSize: 12, color: colors.textSecondary, background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.lg, padding: '12px 16px', marginBottom: 16 }}>
        <i className="ti ti-flame" style={{ marginRight: 6 }}></i>
        Belum ada video channel acuan yang meledak dalam 30 hari terakhir. Channel yang dipantau dicek otomatis tiap 2 jam.
      </div>
    );
  }
  return (
    <div style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.lg, padding: 16, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary, display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
          <i className="ti ti-flame" style={{ color: colors.warning }}></i>Video baru meledak di channel acuan
          <span style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary }}>({alerts.length})</span>
        </div>
        <button type="button" onClick={onDismissAll} title="Tutup semua pemberitahuan ini"
          style={{ fontSize: 12, fontWeight: 600, padding: '5px 10px', borderRadius: 8, border: `1px solid ${colors.border}`, background: 'transparent', color: colors.textSecondary, cursor: 'pointer', flexShrink: 0 }}>
          <i className="ti ti-trash" style={{ marginRight: 4 }}></i>Hapus semua
        </button>
      </div>
      <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 2, marginBottom: 8 }}>
        Views per harinya minimal 3× lipat dari biasanya. Lihat lagu, judul, dan thumbnail-nya untuk ide proyek berikutnya. Klik × untuk menutup yang sudah lewat.
      </div>
      {alerts.slice(0, 10).map(a => (
        <div key={a.id} style={{ display: 'flex', gap: 8, alignItems: 'center', borderTop: `1px solid ${colors.border}` }}>
          <a href={`https://www.youtube.com/watch?v=${a.video_id}`} target="_blank" rel="noopener noreferrer"
            style={{ display: 'flex', gap: 10, alignItems: 'baseline', padding: '8px 0', textDecoration: 'none', color: colors.textPrimary, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
            {!a.seen_at && <span style={{ fontSize: 10, fontWeight: 700, color: '#FFFFFF', background: colors.danger, borderRadius: 4, padding: '1px 6px', flexShrink: 0 }}>BARU</span>}
            <span style={{ fontSize: 12, fontWeight: 600, flexShrink: 0 }}>{a.channel_name}</span>
            <span style={{ fontSize: 12, flex: 1, minWidth: 180, overflowWrap: 'anywhere' }}>{a.title}</span>
            <span style={{ fontSize: 11, color: colors.textSecondary, flexShrink: 0 }}>
              {fmt(a.views)} views · {Number(a.x_baseline).toLocaleString('id-ID')}× · {ago(a.published_at)}
            </span>
          </a>
          <button type="button" onClick={() => onDismiss(a.id)} title="Tutup pemberitahuan ini" aria-label="Tutup pemberitahuan ini"
            style={{ width: 28, height: 28, flexShrink: 0, borderRadius: 8, border: `1px solid ${colors.border}`, background: 'transparent', color: colors.textSecondary, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, lineHeight: 1 }}>
            ×
          </button>
        </div>
      ))}
      {alerts.length > 10 && (
        <div style={{ fontSize: 11, color: colors.textSecondary, paddingTop: 8, borderTop: `1px solid ${colors.border}` }}>
          +{alerts.length - 10} pemberitahuan lain — tutup yang sudah lewat untuk melihatnya.
        </div>
      )}
    </div>
  );
}

export default function ChannelDna({ onAlertsSeen, onUseInUgc }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [url, setUrl] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);
  const [details, setDetails] = useState({});
  const [ytChannels, setYtChannels] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const pollRef = useRef(null);

  // Ambil alert, lalu tandai sudah dilihat di server. Label "BARU" tetap tampil selama
  // halaman ini terbuka karena state lokal tidak ikut diubah.
  // onAlertsSeen dikirim sebagai fungsi inline dari Dashboard (baru tiap render), jadi
  // disimpan di ref supaya alert hanya diambil sekali saat halaman dibuka.
  const seenCb = useRef(onAlertsSeen);
  seenCb.current = onAlertsSeen;
  useEffect(() => {
    axios.get(`${API}/api/dna/alerts`)
      .then(r => {
        setAlerts(r.data?.alerts || []);
        if (r.data?.unseen) axios.post(`${API}/api/dna/alerts/seen`).then(() => seenCb.current && seenCb.current()).catch(() => {});
      })
      .catch(() => {});
  }, []);

  const toggleMonitor = async (row) => {
    try {
      await axios.patch(`${API}/api/dna/${row.id}`, { monitor: !row.monitor });
      setList(l => l.map(x => x.id === row.id ? { ...x, monitor: !row.monitor } : x));
    } catch (e) {
      setError('Gagal mengubah pantauan');
    }
  };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/dna`);
      setList(r.data || []);
      return r.data || [];
    } catch (e) {
      setError('Gagal memuat daftar DNA');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Tutup pemberitahuan "meledak" (2026-10-25): langsung hilang dari daftar; bila gagal, muat ulang
  // dari server (toast global sudah menampilkan error POST).
  const muatUlangAlert = () => axios.get(`${API}/api/dna/alerts`).then(r => setAlerts(r.data?.alerts || [])).catch(() => {});
  const tutupAlert = (id) => {
    setAlerts(a => a.filter(x => x.id !== id));
    axios.post(`${API}/api/dna/alerts/${id}/dismiss`).catch(muatUlangAlert);
  };
  const tutupSemuaAlert = () => {
    if (!window.confirm('Hapus semua pemberitahuan video meledak? Pemberitahuan baru tetap muncul saat ada video meledak berikutnya.')) return;
    setAlerts([]);
    axios.post(`${API}/api/dna/alerts/dismiss-all`).catch(muatUlangAlert);
  };

  const loadDetail = useCallback(async (id) => {
    try {
      const r = await axios.get(`${API}/api/dna/${id}`);
      setDetails(d => ({ ...d, [id]: r.data }));
    } catch (e) { /* toast global sudah menampilkan error */ }
  }, []);

  const reloadChannels = useCallback(() => {
    axios.get(`${API}/api/branding/youtube`).then(r => setYtChannels(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); reloadChannels(); }, [load, reloadChannels]);

  // Selama ada analisa berjalan, cek ulang tiap 3 detik.
  useEffect(() => {
    const busy = list.some(x => x.status === 'processing');
    clearTimeout(pollRef.current);
    if (busy) {
      pollRef.current = setTimeout(async () => {
        const fresh = await load();
        fresh.filter(x => x.status !== 'processing' && details[x.id]?.status === 'processing').forEach(x => loadDetail(x.id));
        if (openId && fresh.find(x => x.id === openId)?.status === 'ready' && !details[openId]?.profile) loadDetail(openId);
      }, 3000);
    }
    return () => clearTimeout(pollRef.current);
  }, [list, load, loadDetail, details, openId]);

  const add = async () => {
    if (!url.trim()) return;
    setAdding(true); setError('');
    try {
      const r = await axios.post(`${API}/api/dna`, { url: url.trim() });
      setUrl('');
      setOpenId(r.data.id);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Gagal menambahkan channel');
    }
    setAdding(false);
  };

  const toggle = (row) => {
    if (openId === row.id) { setOpenId(null); return; }
    setOpenId(row.id);
    if (row.status === 'ready' && !details[row.id]?.profile) loadDetail(row.id);
  };

  const refresh = async (row) => {
    setError('');
    try {
      await axios.post(`${API}/api/dna/${row.id}/refresh`);
      setDetails(d => ({ ...d, [row.id]: { ...(d[row.id] || {}), status: 'processing' } }));
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Gagal memperbarui data');
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Hapus DNA "${row.channel_name || row.channel_url}"? Proyek UGC yang memakainya tetap aman, hanya tidak lagi mengikuti DNA ini.`)) return;
    try {
      await axios.delete(`${API}/api/dna/${row.id}`);
      if (openId === row.id) setOpenId(null);
      load();
    } catch (e) {
      setError('Gagal menghapus DNA');
    }
  };

  return (
    <div style={{ paddingBottom: 40 }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: colors.textPrimary, display: 'flex', alignItems: 'center', gap: 8 }}>
          <i className="ti ti-dna" style={{ fontSize: 22 }}></i>DNA Channel
        </h1>
        <p style={{ fontSize: 13, color: colors.textSecondary, marginTop: 2, maxWidth: 640 }}>
          Tempel link channel YouTube yang sedang naik. Sistem membaca pola judul, hashtag, durasi, jam upload, dan video terlarisnya, lalu pola itu bisa dipakai untuk judul UGC dan jadwal posting kamu.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <input type="text" placeholder="https://www.youtube.com/@namachannel" value={url}
          onChange={e => setUrl(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') add(); }}
          style={{ flex: 1, minWidth: 220 }} />
        <button onClick={add} disabled={adding || !url.trim()} className="btn btn-gold"
          style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, opacity: (adding || !url.trim()) ? 0.6 : 1 }}>
          <i className="ti ti-search" style={{ fontSize: 15 }}></i>{adding ? 'Menambahkan...' : 'Analisa'}
        </button>
      </div>

      {error && (
        <div style={{ padding: '10px 14px', background: colors.dangerMuted, color: colors.danger, borderRadius: radius.md, marginBottom: 16, fontSize: 13 }}>{error}</div>
      )}

      {list.length > 0 && <AlertsBox alerts={alerts} onDismiss={tutupAlert} onDismissAll={tutupSemuaAlert} />}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: colors.textSecondary }}>Memuat DNA...</div>
      ) : list.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: colors.textSecondary, background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.lg }}>
          <i className="ti ti-dna" style={{ fontSize: 40, marginBottom: 10, display: 'block' }}></i>
          Belum ada channel acuan. Tempel link channel di atas, misalnya https://www.youtube.com/@pusat.rampak
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {list.map(row => {
            const det = details[row.id];
            return (
              <div key={row.id} style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.lg, overflow: 'hidden' }}>
                <div onClick={() => toggle(row)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 16, cursor: 'pointer', flexWrap: 'wrap' }}>
                  <i className={`ti ${openId === row.id ? 'ti-chevron-down' : 'ti-chevron-right'}`} style={{ fontSize: 16, color: colors.textSecondary }}></i>
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary }}>{row.channel_name || row.channel_url.replace('https://www.youtube.com/', '')}</div>
                    <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2 }}>
                      {row.status === 'ready'
                        ? `${fmt(row.subscribers)} subscriber · median ${fmt(row.median_views)} views${row.best_hour_wib != null ? ` · jam terbaik ${hh(row.best_hour_wib)} WIB` : ''}`
                        : row.status === 'failed' ? (row.error_message || 'Analisa gagal') : 'Sedang membaca data publik channel, biasanya kurang dari satu menit'}
                    </div>
                    {row.status === 'ready' && (
                      <label onClick={e => e.stopPropagation()} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: colors.textSecondary, marginTop: 6, cursor: 'pointer' }}>
                        {/* App.css memberi semua input width:100% + padding — wajib dinetralkan di checkbox. */}
                        <input type="checkbox" checked={!!row.monitor} onChange={() => toggleMonitor(row)}
                          style={{ width: 14, height: 14, padding: 0, margin: 0, flexShrink: 0, accentColor: colors.accent }} />
                        Pantau otomatis{row.monitor ? ` · dicek ${ago(row.last_checked_at)}` : ' (mati)'}
                      </label>
                    )}
                  </div>
                  {row.unseen_alerts > 0 && (
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 6, background: colors.warningMuted, color: colors.warning, display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                      <i className="ti ti-flame" style={{ fontSize: 13 }}></i>{row.unseen_alerts} meledak
                    </span>
                  )}
                  {row.status === 'ready' ? <WorthPill w={row.worthiness} /> : <StatusPill status={row.status} />}
                  <a href={row.channel_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} title="Buka channel" style={{ color: colors.textSecondary, padding: 4 }}>
                    <i className="ti ti-external-link" style={{ fontSize: 16 }}></i>
                  </a>
                  {row.status !== 'processing' && (
                    <button onClick={(e) => { e.stopPropagation(); refresh(row); }} title="Perbarui data" style={{ background: 'transparent', border: 'none', color: colors.textSecondary, cursor: 'pointer', padding: 4 }}>
                      <i className="ti ti-refresh" style={{ fontSize: 16 }}></i>
                    </button>
                  )}
                  <button onClick={(e) => { e.stopPropagation(); remove(row); }} title="Hapus" style={{ background: 'transparent', border: 'none', color: colors.danger, cursor: 'pointer', padding: 4 }}>
                    <i className="ti ti-trash" style={{ fontSize: 16 }}></i>
                  </button>
                </div>
                {openId === row.id && (
                  <div style={{ padding: '0 16px 16px', borderTop: `1px solid ${colors.border}` }}>
                    {row.status === 'processing' && <div style={{ fontSize: 12, color: colors.textSecondary, paddingTop: 12 }}>Menganalisa... halaman ini akan terisi otomatis.</div>}
                    {row.status === 'failed' && <div style={{ fontSize: 12, color: colors.danger, paddingTop: 12 }}>{row.error_message || 'Analisa gagal.'} Coba tombol perbarui.</div>}
                    {row.status === 'ready' && (det?.profile
                      ? <DnaDetail dna={det} ytChannels={ytChannels} reloadChannels={reloadChannels} onUseInUgc={onUseInUgc} />
                      : <div style={{ fontSize: 12, color: colors.textSecondary, paddingTop: 12 }}>Memuat detail...</div>)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
